import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { BadgeSuccess, PageHeader, StepActions } from "@components/ui_v2";

/** Datos de ejemplo del diseño (`S_ExpReview` en modo factoraje de Flux); aún no se leen del store. */
const RESUMEN_LINEA = [
  { label: "Línea preaprobada", value: "$3,100,000" },
  { label: "Aforo", value: "90%" },
  { label: "Tasa anual", value: "21.6%" },
  { label: "Comisión por operación", value: "1.2%" },
];
const PLAZO_MAX_FACTURA = 120;

const PASOS_COMPLETADOS = [
  { label: "Solicitud", sub: "Completada hace 2 días" },
  { label: "Empresa", sub: "13 documentos validados" },
  { label: "Personas relacionadas", sub: "3 personas · información completa" },
];

interface StepRowProps {
  label: string;
  sub: string;
}

/**
 * `StepRow` (estado `done`) del diseño: círculo verde de 22px con check, etiqueta con su descripción y la
 * pastilla "Completado" a la derecha.
 */
const StepRow = ({ label, sub }: StepRowProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "success.main",
        color: "#fff",
      }}
    >
      <Check sx={{ fontSize: 13 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "primary.dark" }}>
        {label}
      </Typography>
      <Typography variant="body3" sx={{ color: "tokens.textTertiary" }}>
        {sub}
      </Typography>
    </Box>
    <BadgeSuccess>Completado</BadgeSuccess>
  </Box>
);

/**
 * Expediente enviado a revisión (`S_ExpReview` del diseño): avance de las secciones del expediente y el
 * resumen de la línea preaprobada. Solo maquetado.
 */
export const RevisionExpedienteResumen = () => (
  <Box>
    <PageHeader
      eyebrow="Expediente · Estado"
      title="Tu expediente está en revisión"
      subtitle="Estamos validando los documentos y la información que enviaste. Te avisaremos por correo y WhatsApp en cuanto haya novedades."
    />

    <Paper variant="lendia_paper">
      <LinearProgress
        variant="determinate"
        value={100}
        data-testid="expediente-revision-progress"
        sx={{
          mb: "22px",
          height: 4,
          borderRadius: 999,
          bgcolor: (theme) => theme.palette.tokens?.neutralSoftBg,
          "& .MuiLinearProgress-bar": { borderRadius: 999 },
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {PASOS_COMPLETADOS.map((paso) => (
          <StepRow key={paso.label} label={paso.label} sub={paso.sub} />
        ))}
      </Box>
    </Paper>

    <Paper variant="lendia_paper" sx={{ mt: "18px", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: "12px" }}>
        <Typography
          variant="body3"
          sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "primary.main" }}
        >
          Resumen de tu línea
        </Typography>
        <BadgeSuccess>Preaprobada</BadgeSuccess>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "14px 24px" }}>
        {RESUMEN_LINEA.map((dato) => (
          <Box key={dato.label}>
            <Typography
              variant="body3"
              component="div"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "tokens.textTertiary",
                mb: "3px",
              }}
            >
              {dato.label}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontFamily: "Outfit", fontWeight: 600, color: "primary.dark", fontVariantNumeric: "tabular-nums" }}
            >
              {dato.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="body3" component="p" sx={{ color: "tokens.textTertiary", mt: "17px" }}>
        Plazo máximo de factura <strong>{PLAZO_MAX_FACTURA} días</strong> · la línea se libera conforme te pagan tus
        clientes.
      </Typography>
    </Paper>

    {/* Sin navegación todavía: solo maquetado. */}
    <StepActions
      onBack={() => undefined}
      onNext={() => undefined}
      nextLabel="Ver resultado final"
      backTestId="expediente-revision-back-button"
      nextTestId="expediente-revision-continue-button"
    />
  </Box>
);
